import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { MuiTelInput } from "mui-tel-input";

import {
  registerApplicant,
  sendApplicantOtp,
  verifyApplicantOtp,
  checkApplicantContact,
  applicantErrorMessage,
  extractExperienceCertificate,
  extractEducationCertificate,
} from "../../services/backend/ApplicantService";
import {
  sendPhoneCode,
  confirmPhoneCode,
  phoneAuthErrorMessage,
  toE164,
  isValidPhone,
  clearRecaptcha,
} from "../../services/auth/PhoneVerification";
import { NO_SMS_HINT, SMS_TEST_MODE_VISIBLE, phoneSendInfo, useResendCooldown } from "./phoneGate";
import { getOrgName, DEFAULT_ORG_NAME } from "../../services/backend/AppConfigService";
import {
  getApplicantConfig,
  DEFAULT_APPLICANT_CONFIG,
  docsForStage,
  institutionOptions,
  openInstitutionOptions,
  openLevelsOf,
  academicLevelsOf,
  profileDocs,
  profileDocForSection,
} from "../../services/backend/ApplicantConfigService";
import {
  buildApplicantData,
  emptyEducation,
  emptyExperience,
  hasEducationEntry,
  hasExperienceEntry,
} from "./applicantPayload";
import { entryNeedsEvidence } from "../applicants/applicantLogic";
import { EducationList, ExperienceList } from "./ProfileEntryLists";

// Hidden container id hosting the invisible reCAPTCHA for Firebase Phone Auth.
const RECAPTCHA_ID = "recaptcha-applicant-registro";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Number of upload slots to render for a document. config maxFiles: a positive
// number is a fixed cap; 0 means unlimited, so we always show one spare empty
// slot beyond what's already attached.
const slotsFor = (docDef, attachedCount = 0) => {
  const n = Math.floor(Number(docDef?.maxFiles));
  if (n === 0) return attachedCount + 1; // unlimited
  return Number.isFinite(n) && n > 0 ? n : 1;
};

// Habeas-data text; the institution name comes from config/global.orgName.
const buildHabeas = (orgName) =>
  `Autorizo el tratamiento de mis datos personales por parte de ${orgName} para fines académicos y administrativos, conforme a la Ley 1581 de 2012. Puedo conocer, actualizar, rectificar y solicitar la eliminación de mi información.`;

// Every public registration enters the permanent "General" registry; admins may
// later open other calls the aspirant can apply to from their profile.
const GENERAL_POSTULACION = "general";

export default function ApplicantRegistrationForm() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedProfile, setSavedProfile] = useState(false);
  const [error, setError] = useState(null);
  // Confirmation before leaving step 0: the identity fields below can't be changed
  // later, so we make the applicant acknowledge them once.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [orgName, setOrgName] = useState(DEFAULT_ORG_NAME);
  // Applicants config (documents, ID types, academic levels, max file size) —
  // the live source of truth (config/applicants). Seeded with defaults so the
  // form renders before the async load resolves.
  const [cfg, setCfg] = useState(DEFAULT_APPLICANT_CONFIG);
  const idTypes = cfg.idTypes || DEFAULT_APPLICANT_CONFIG.idTypes;
  const niveles = useMemo(() => academicLevelsOf(cfg), [cfg]);
  const instituciones = useMemo(() => institutionOptions(cfg), [cfg]);
  const institucionesAbiertas = useMemo(() => openInstitutionOptions(cfg), [cfg]);
  const nivelesAbiertos = useMemo(() => openLevelsOf(cfg), [cfg]);
  const maxFileBytes = cfg.maxFileBytes || DEFAULT_APPLICANT_CONFIG.maxFileBytes;
  const maxFileMb = Math.round(maxFileBytes / 1024 / 1024);

  // Los soportes de formación y experiencia son documentos de PERFIL atados a
  // cada registro: se piden dentro de su sección, no en el paso de documentos.
  // Si el admin desactiva uno, su sección completa desaparece del formulario.
  const eduDoc = useMemo(() => profileDocForSection(cfg, "education"), [cfg]);
  const expDoc = useMemo(() => profileDocForSection(cfg, "workExperience"), [cfg]);
  // Paso "Documentos": documentos de perfil sueltos (cédula…) + los propios de
  // la convocatoria. Cada uno viaja con su alcance.
  const docStepDocs = useMemo(() => [
    ...profileDocs(cfg).filter((d) => !d.boundTo).map((d) => ({ ...d, scope: "profile" })),
    ...docsForStage(cfg, 1).map((d) => ({ ...d, scope: "application" })),
  ], [cfg]);

  const steps = useMemo(() => [
    { key: "personal", label: "Datos personales" },
    ...(eduDoc ? [{ key: "education", label: "Formación" }] : []),
    ...(expDoc ? [{ key: "experience", label: "Experiencia" }] : []),
    ...(docStepDocs.length ? [{ key: "documents", label: "Documentos" }] : []),
    { key: "review", label: "Revisión" },
  ], [eduDoc, expDoc, docStepDocs]);
  const stepKey = steps[activeStep]?.key || "personal";
  const isLastStep = activeStep === steps.length - 1;

  // Email verification gate (reuses the resume-flow OTP machinery). The email
  // must be verified before leaving step 0, so the profile and its magic-link
  // confirmation email are only ever sent to a confirmed inbox.
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpInfo, setOtpInfo] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");

  // Phone verification gate (Firebase Phone Auth). Both email AND phone must be
  // verified before leaving step 0, so every applicant has two reachable channels.
  const [phoneConfirmation, setPhoneConfirmation] = useState(null);
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneInfo, setPhoneInfo] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(""); // E.164
  const [phoneToken, setPhoneToken] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const phoneCooldown = useResendCooldown();

  useEffect(() => {
    getOrgName().then(setOrgName);
    getApplicantConfig().then(setCfg);
  }, []);

  // Tear down the invisible reCAPTCHA when leaving the form.
  useEffect(() => () => clearRecaptcha(), []);

  const [form, setForm] = useState({
    firstName: "", lastName: "", documentType: "Cédula de ciudadanía", nuip: "",
    email: "", mobilePhone: "", address: "", birthDate: "",
    // Both are repeatable; each entry needs its own supporting document.
    education: [emptyEducation()],
    workExperience: [emptyExperience()],
  });
  // Who the documents should belong to, as typed in the first step.
  const identityPerson = { nuip: form.nuip, firstName: form.firstName, lastName: form.lastName };
  const [files, setFiles] = useState({}); // { [type]: (File|undefined)[] }  — one slot per allowed file
  // Soportes por registro: { [docKey]: { [entryId]: (File|undefined)[] } }.
  const [evidence, setEvidence] = useState({});

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Editing the email after verifying invalidates the verification.
  const onEmailChange = (v) => {
    set("email", v);
    if (emailVerified && v.trim() !== verifiedEmail) {
      setEmailVerified(false);
      setOtpSent(false);
      setOtpCode("");
      setOtpInfo(null);
    }
  };

  // Editing the phone invalidates any verification or pending code: a code sent
  // to the old number can't confirm the new one. Correcting a mistyped number is
  // also the fix the "¿no te llegó?" hint asks for, so the cooldown is dropped —
  // it exists to stop hammering ONE number, not to block a corrected one.
  const onPhoneChange = (v) => {
    set("mobilePhone", v);
    if (toE164(v) !== verifiedPhone) {
      setPhoneVerified(false);
      setPhoneConfirmation(null);
      setPhoneCode("");
      setPhoneInfo(null);
      setPhoneToken(null);
      phoneCooldown.reset();
    }
  };

  const handleSendPhoneCode = async () => {
    if (!isValidPhone(form.mobilePhone)) { setPhoneError("Ingresa un número de celular válido."); return; }
    if (!form.nuip.trim()) { setPhoneError("Ingresa tu número de documento primero."); return; }
    setPhoneBusy(true); setPhoneError(null);
    try {
      // Fail fast (before spending an SMS) if the phone belongs to another cédula.
      const chk = await checkApplicantContact({ nuip: form.nuip.trim(), phone: toE164(form.mobilePhone) });
      if (chk.ok && chk.json?.phoneInUse) {
        setPhoneError(applicantErrorMessage("PHONE_IN_USE"));
        setPhoneBusy(false);
        return;
      }
      const confirmation = await sendPhoneCode(form.mobilePhone, RECAPTCHA_ID, { testMode: !!cfg.smsTestMode });
      setPhoneConfirmation(confirmation);
      setPhoneInfo(phoneSendInfo(toE164(form.mobilePhone), !!cfg.smsTestMode));
      phoneCooldown.start();
    } catch (e) {
      setPhoneError(phoneAuthErrorMessage(e));
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!phoneConfirmation) return;
    setPhoneBusy(true); setPhoneError(null);
    try {
      const { idToken } = await confirmPhoneCode(phoneConfirmation, phoneCode);
      setPhoneToken(idToken);
      setPhoneVerified(true);
      setVerifiedPhone(toE164(form.mobilePhone));
      setPhoneInfo(null);
      setPhoneConfirmation(null);
      phoneCooldown.reset();
    } catch (e) {
      setPhoneError(phoneAuthErrorMessage(e));
    } finally {
      setPhoneBusy(false);
    }
  };

  const handleSendOtp = async () => {
    const dest = form.email.trim();
    if (!EMAIL_RE.test(dest)) { setError("Ingresa un correo electrónico válido."); return; }
    if (!form.nuip.trim()) { setError("Ingresa tu número de documento primero."); return; }
    setSubmitting(true); setError(null);
    // Fail fast (before sending the OTP) if the email belongs to another cédula.
    const chk = await checkApplicantContact({ nuip: form.nuip.trim(), email: dest });
    if (chk.ok && chk.json?.emailInUse) {
      setSubmitting(false);
      setError(applicantErrorMessage("EMAIL_IN_USE"));
      return;
    }
    const { ok, json } = await sendApplicantOtp({ recipient: dest });
    setSubmitting(false);
    if (!ok) { setError(json?.error || "No fue posible enviar el código."); return; }
    setOtpSent(true);
    setOtpInfo(`Enviamos un código a ${dest}. Vence en ${json?.expiresInMinutes || 10} minutos.`);
  };

  const handleVerifyOtp = async () => {
    setSubmitting(true); setError(null);
    const { ok, json } = await verifyApplicantOtp({ recipient: form.email.trim(), code: otpCode.trim() });
    setSubmitting(false);
    if (!ok) {
      setError(json?.error === "OTP_EXPIRED" ? "El código expiró. Solicita uno nuevo." : "Código incorrecto.");
      return;
    }
    setEmailVerified(true);
    setVerifiedEmail(form.email.trim());
    setOtpInfo(null);
  };

  const validateStep = (key) => {
    if (key === "personal") {
      return !!(form.firstName && form.lastName && form.nuip && form.email && checked
        && emailVerified && form.email.trim() === verifiedEmail
        && phoneVerified && toE164(form.mobilePhone) === verifiedPhone);
    }
    // Al menos una formación con título e institución; las demás son opcionales.
    if (key === "education") return form.education.some((e) => e.degree && e.institution);
    return true; // experiencia + documents opcionales para postular
  };

  // Persist the profile once, when leaving step 0. Creates the postulación
  // record (so the application can be resumed) and triggers the confirmation
  // email with the magic link.
  const persistProfile = async () => {
    if (savedProfile) return true;
    setSubmitting(true);
    setError(null);
    const { ok, json } = await registerApplicant({
      data: buildApplicantData(form), callId: GENERAL_POSTULACION, stage: 1, step: "perfil",
      idempotencyKey: `applicant:${form.nuip}:perfil:${GENERAL_POSTULACION}`,
      phoneToken,
    });
    setSubmitting(false);
    if (!ok) {
      setError(applicantErrorMessage(json?.error) || json?.error || "No fue posible guardar tus datos. Intenta de nuevo.");
      return false;
    }
    setSavedProfile(true);
    return true;
  };

  const handleNext = async () => {
    if (!validateStep(stepKey)) return;
    // Leaving step 0 persists the profile and locks the identity fields; confirm
    // first so the applicant knows name/type/número won't be editable afterward.
    if (stepKey === "personal") { setConfirmOpen(true); return; }
    setActiveStep((s) => s + 1);
  };
  const handleConfirmPermanentes = async () => {
    setConfirmOpen(false);
    const ok = await persistProfile();
    if (!ok) return;
    setActiveStep((s) => s + 1);
  };
  const handleBack = () => setActiveStep((s) => s - 1);

  const handleFile = (type, slot, file) => {
    if (file && file.size > maxFileBytes) {
      setError(`El archivo "${file.name}" supera el límite de ${maxFileMb} MB.`);
      return;
    }
    setError(null);
    setFiles((prev) => {
      const arr = Array.isArray(prev[type]) ? [...prev[type]] : [];
      arr[slot] = file || undefined;
      return { ...prev, [type]: arr };
    });
  };

  // Un soporte se guarda contra el id del registro que certifica. `replaceFileId`
  // sólo puede venir con algo ya guardado, que en un registro nuevo nunca existe;
  // se acepta igual porque la lista de registros es la misma que usa el perfil.
  const handleEvidence = (docKey) => (entryId, slot, file, replaceFileId = null) => {
    if (file && file.size > maxFileBytes) {
      setError(`El archivo "${file.name}" supera el límite de ${maxFileMb} MB.`);
      return;
    }
    setError(null);
    setEvidence((prev) => {
      const byEntry = { ...(prev[docKey] || {}) };
      const arr = Array.isArray(byEntry[entryId]) ? [...byEntry[entryId]] : [];
      arr[slot] = file ? { file, replaceFileId } : undefined;
      byEntry[entryId] = arr;
      return { ...prev, [docKey]: byEntry };
    });
  };
  const pendingEvidenceFor = (docKey) => (entryId) => (evidence[docKey]?.[entryId]) || [];

  // Filled entries per list, used by the review step.
  const educationEntries = form.education.filter(hasEducationEntry);
  const experienceEntries = form.workExperience.filter(hasExperienceEntry);

  // Flatten everything picked into the [{ type, file, scope, entryId }] list the
  // intake expects. Evidence is filtered against the entries that will actually
  // be saved: a certificate attached to a row the applicant then deleted (or
  // blanked) has nothing to certify and the backend would reject the whole
  // submission over it.
  const flattenFiles = () => {
    const list = [];
    docStepDocs.forEach((d) => {
      (files[d.key] || []).forEach((file) => { if (file) list.push({ type: d.key, file, scope: d.scope }); });
    });
    const liveEntries = {
      ...(eduDoc ? { [eduDoc.key]: new Set(educationEntries.map((e) => e.id)) } : {}),
      ...(expDoc ? { [expDoc.key]: new Set(experienceEntries.map((e) => e.id)) } : {}),
    };
    Object.entries(liveEntries).forEach(([docKey, alive]) => {
      Object.entries(evidence[docKey] || {}).forEach(([entryId, arr]) => {
        if (!alive.has(entryId)) return;
        (arr || []).forEach((picked) => {
          if (picked?.file) {
            list.push({
              type: docKey, file: picked.file, scope: "profile", entryId,
              replaceFileId: picked.replaceFileId,
            });
          }
        });
      });
    });
    return list;
  };
  const attachedTipos = docStepDocs
    .filter((d) => (files[d.key] || []).some(Boolean))
    .map((d) => `${d.label} (${(files[d.key] || []).filter(Boolean).length})`);
  const attachedEvidenceCount = (docKey) =>
    Object.values(evidence[docKey] || {}).reduce((n, arr) => n + (arr || []).filter(Boolean).length, 0);
  // Registros que deben soporte y aún no lo tienen (los "en curso" / "actualmente"
  // no deben ninguno).
  const missingEvidence = (doc, entries, section) => {
    if (!doc) return 0;
    return entries.filter((e) =>
      entryNeedsEvidence(e, section) && !(evidence[doc.key]?.[e.id] || []).some(Boolean)
    ).length;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    const fileList = flattenFiles();
    const { ok, json } = await registerApplicant({
      data: buildApplicantData(form), callId: GENERAL_POSTULACION, stage: 1, step: "documents", files: fileList,
      idempotencyKey: `applicant:${form.nuip}:docs:${Date.now()}`,
    });
    setSubmitting(false);
    if (!ok) {
      setError(applicantErrorMessage(json?.error) || "No fue posible enviar tu postulación. Intenta de nuevo.");
      return;
    }
    // Take the aspirant straight to their profile (pre-resolved via router state,
    // so no OTP) where they can review their data and apply to other calls.
    navigate("/submission/continuar", { state: { nuip: form.nuip, justRegistered: true } });
  };

  const getStepContent = (step) => {
    if (step === "personal") {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Nombres" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Apellidos" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Tipo de documento</InputLabel>
              <Select label="Tipo de documento" value={form.documentType} onChange={(e) => set("documentType", e.target.value)}>
                {idTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Número de documento" value={form.nuip} onChange={(e) => set("nuip", e.target.value.replace(/\D/g, ""))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required type="email" label="Correo electrónico" value={form.email} onChange={(e) => onEmailChange(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            {!emailVerified ? (
              <Stack spacing={1.5}>
                {otpInfo && <Alert severity="info">{otpInfo}</Alert>}
                {!otpSent ? (
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSendOtp}
                      disabled={submitting || !EMAIL_RE.test(form.email.trim()) || !form.nuip.trim()}
                    >
                      {submitting ? "Enviando…" : "Enviar código de verificación"}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Verifica tu correo para continuar. Te enviaremos un código de 6 dígitos.
                    </Typography>
                  </Box>
                ) : (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                    <TextField
                      label="Código de 6 dígitos"
                      size="small"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    />
                    <Button variant="contained" size="small" onClick={handleVerifyOtp} disabled={submitting || otpCode.length !== 6}>
                      {submitting ? "Verificando…" : "Verificar"}
                    </Button>
                    <Button variant="text" size="small" onClick={handleSendOtp} disabled={submitting}>
                      Reenviar código
                    </Button>
                  </Stack>
                )}
              </Stack>
            ) : (
              <Alert severity="success" sx={{ py: 0.5 }}>Correo verificado ✓</Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <MuiTelInput
              fullWidth
              required
              label="Celular"
              defaultCountry="CO"
              preferredCountries={["CO", "US", "ES", "MX", "PE", "EC", "VE"]}
              value={form.mobilePhone}
              onChange={onPhoneChange}
              disabled={phoneVerified}
            />
          </Grid>
          <Grid item xs={12}>
            {!phoneVerified ? (
              <Stack spacing={1.5}>
                {SMS_TEST_MODE_VISIBLE && cfg.smsTestMode && (
                  <Alert severity="warning">
                    Modo de prueba SMS activo: no se envían SMS reales. Solo funcionan los
                    números de prueba registrados en Firebase.
                  </Alert>
                )}
                {phoneError && <Alert severity="error">{phoneError}</Alert>}
                {phoneInfo && <Alert severity="info">{phoneInfo}</Alert>}
                {!phoneConfirmation ? (
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSendPhoneCode}
                      disabled={phoneBusy || !isValidPhone(form.mobilePhone) || !form.nuip.trim()}
                    >
                      {phoneBusy ? "Enviando…" : "Enviar código por SMS"}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Verifica tu celular para continuar. Te enviaremos un código de 6 dígitos por SMS.
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                      <TextField
                        label="Código de 6 dígitos"
                        size="small"
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      />
                      <Button variant="contained" size="small" onClick={handleVerifyPhoneCode} disabled={phoneBusy || phoneCode.length !== 6}>
                        {phoneBusy ? "Verificando…" : "Verificar"}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleSendPhoneCode}
                        disabled={phoneBusy || phoneCooldown.seconds > 0}
                      >
                        {phoneCooldown.seconds > 0 ? `Reenviar en ${phoneCooldown.seconds}s` : "Reenviar código"}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {NO_SMS_HINT}
                    </Typography>
                  </Box>
                )}
              </Stack>
            ) : (
              <Alert severity="success" sx={{ py: 0.5 }}>Celular verificado ✓</Alert>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="Fecha de nacimiento" InputLabelProps={{ shrink: true }} value={form.birthDate} onChange={(e) => set("birthDate", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Dirección" value={form.address} onChange={(e) => set("address", e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} size="small" />}
              label={<Typography sx={{ fontSize: 12 }}>{buildHabeas(orgName)}</Typography>}
            />
          </Grid>
        </Grid>
      );
    }
    if (step === "education") {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Registra al menos una formación (título e institución son obligatorios). Adjunta el
            soporte de cada una dentro de su propio registro.
          </Typography>
          <EducationList
            items={form.education}
            levels={niveles}
            institutions={instituciones}
            openInstitutions={institucionesAbiertas}
            openLevels={nivelesAbiertos}
            onChange={(next) => set("education", next)}
            evidenceDoc={eduDoc}
            pendingFor={pendingEvidenceFor(eduDoc?.key)}
            onPickEvidence={handleEvidence(eduDoc?.key)}
            onExtractCertificate={cfg?.educationAI?.enabled !== false ? extractEducationCertificate : undefined}
            person={identityPerson}
          />
        </>
      );
    }
    if (step === "experience") {
      return (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Opcional. Registra todas las experiencias que puedas certificar, cada una con su
            certificado laboral.
          </Typography>
          <ExperienceList
            items={form.workExperience}
            onChange={(next) => set("workExperience", next)}
            evidenceDoc={expDoc}
            pendingFor={pendingEvidenceFor(expDoc?.key)}
            onPickEvidence={handleEvidence(expDoc?.key)}
            onExtractCertificate={cfg?.experienceAI?.enabled !== false ? extractExperienceCertificate : undefined}
            person={identityPerson}
          />
        </>
      );
    }
    if (step === "documents") {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Adjunta estos documentos (PDF o imagen, máx. {maxFileMb} MB). Los soportes de tu
              formación y tu experiencia se cargan dentro de cada registro, en los pasos anteriores.
            </Typography>
          </Grid>
          {docStepDocs.map((docDef) => {
            const { key: type, label, description, acceptedMime } = docDef;
            const accept = (acceptedMime && acceptedMime.length ? acceptedMime : ["application/pdf", "image/*"]).join(",");
            const current = files[type] || [];
            const slots = slotsFor(docDef, current.filter(Boolean).length);
            return (
              <Grid item xs={12} sm={6} key={type}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {label}{docDef.required === false ? " (opcional)" : ""}
                </Typography>
                {description && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{description}</Typography>
                )}
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  {Array.from({ length: slots }).map((_, slot) => (
                    <Box key={slot}>
                      <Button component="label" variant="outlined" size="small" startIcon={<AttachFileIcon />} fullWidth sx={{ justifyContent: "flex-start" }}>
                        {slots > 1 ? `Archivo ${slot + 1}` : "Adjuntar archivo"}
                        <input hidden type="file" accept={accept} onChange={(e) => handleFile(type, slot, e.target.files?.[0] || null)} />
                      </Button>
                      {current[slot] && <Chip sx={{ mt: 0.5 }} size="small" label={current[slot].name} onDelete={() => handleFile(type, slot, null)} />}
                    </Box>
                  ))}
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      );
    }
    // Revisión
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}><Typography variant="subtitle1">Revisa tu información antes de enviar</Typography></Grid>
        <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Nombre</Typography><Typography>{form.firstName} {form.lastName}</Typography></Grid>
        <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Documento</Typography><Typography>{form.documentType} {form.nuip}</Typography></Grid>
        <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Correo</Typography><Typography>{form.email}</Typography></Grid>
        <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary">Celular</Typography><Typography>{toE164(form.mobilePhone) || "—"}</Typography></Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Formación ({educationEntries.length})</Typography>
          {educationEntries.length === 0 ? (
            <Typography>—</Typography>
          ) : (
            educationEntries.map((e, i) => (
              <Typography key={i} variant="body2">
                {[
                  e.degree,
                  e.institution,
                  e.level,
                  e.inProgress ? "En curso" : null,
                  e.foreignDegree ? (e.convalidated ? "Título extranjero convalidado" : "Título extranjero SIN convalidar") : null,
                ].filter(Boolean).join(" · ") || "—"}
              </Typography>
            ))
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Experiencia ({experienceEntries.length})</Typography>
          {experienceEntries.length === 0 ? (
            <Typography>—</Typography>
          ) : (
            experienceEntries.map((e, i) => (
              <Typography key={i} variant="body2">
                {[e.position, e.company, e.current ? "Actualmente" : null].filter(Boolean).join(" · ") || "—"}
              </Typography>
            ))
          )}
        </Grid>
        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Documentos adjuntos</Typography>
          <Typography>
            {[
              ...attachedTipos,
              ...(eduDoc && attachedEvidenceCount(eduDoc.key)
                ? [`${eduDoc.label} (${attachedEvidenceCount(eduDoc.key)})`] : []),
              ...(expDoc && attachedEvidenceCount(expDoc.key)
                ? [`${expDoc.label} (${attachedEvidenceCount(expDoc.key)})`] : []),
            ].join(", ") || "Ninguno (puedes cargarlos luego)"}
          </Typography>
        </Grid>
        {(missingEvidence(eduDoc, educationEntries, "education") > 0
          || missingEvidence(expDoc, experienceEntries, "workExperience") > 0) && (
          <Grid item xs={12}>
            <Alert severity="warning">
              Hay registros sin soporte documental. Puedes enviar tu postulación ahora y cargar los
              documentos faltantes después desde tu perfil, pero solo se tendrán en cuenta los
              estudios y las experiencias que estén certificados.
            </Alert>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Registro de postulación
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
        Contratación de Extensión
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
        {steps.map((s) => (
          <Step key={s.key}><StepLabel>{s.label}</StepLabel></Step>
        ))}
      </Stepper>

      <Paper elevation={2} sx={{ p: { xs: 2, md: 4 } }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {getStepContent(stepKey)}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button disabled={activeStep === 0 || submitting} onClick={handleBack}>Atrás</Button>
          {isLastStep ? (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Enviando…" : "Confirmar y enviar"}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext} disabled={submitting || !validateStep(stepKey)}>
              {submitting ? "Guardando…" : "Siguiente"}
            </Button>
          )}
        </Box>
      </Paper>
      {/* Invisible reCAPTCHA host for Firebase Phone Auth. */}
      <div id={RECAPTCHA_ID} />

      {/* Permanent-data acknowledgement before persisting the profile (step 0). */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirma tus datos permanentes</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Estos datos <strong>no podrás modificarlos</strong> después de continuar. Verifica que
            estén correctos:
          </DialogContentText>
          <Stack spacing={1}>
            <Box>
              <Typography variant="caption" color="text.secondary">Nombre completo</Typography>
              <Typography>{form.firstName} {form.lastName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Tipo de documento</Typography>
              <Typography>{form.documentType}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Número de documento</Typography>
              <Typography>{form.nuip}</Typography>
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
            El correo, el celular y los demás datos sí podrás editarlos luego desde tu perfil.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Revisar</Button>
          <Button variant="contained" onClick={handleConfirmPermanentes} disabled={submitting}>
            {submitting ? "Guardando…" : "Confirmar y continuar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
